// Environment configuration for Aitryx Partner Portal
// Enterprise-grade security: Uses AitryxCore JWT tokens for all API communication
//
// IMPORTANT: This file no longer contains hardcoded fallback values.
// All required environment variables must be set via .env files or CI/CD.

export interface EnvironmentConfig {
  api: {
    baseUrl: string;
    telemetryUrl: string;
    marketplaceUrl: string;
    timeout: number;
  };
  azure: {
    clientId: string;
    authority: string;
    redirectUri: string;
    apiScope: string;
  };
  storage: {
    sharedAccount: string;
  };
  features: {
    enableMsalLogging: boolean;
    enableApiLogging: boolean;
    mockDataMode: boolean;
    devMode: boolean; // Bypasses authentication for local UI development
  };
  environment: 'development' | 'beta' | 'production';
}

// Helper function to require environment variables
// Throws an error if the variable is not set (fails explicitly instead of silently using defaults)
function requireEnv(key: string): string {
  const value = process.env[key];
  if (!value) {
    throw new Error(
      `Missing required environment variable: ${key}\n` +
      `Please ensure you have a valid .env file (e.g., .env.development, .env.beta, .env.production)`
    );
  }
  return value;
}

// Helper function for optional environment variables with defaults
function optionalEnv(key: string, defaultValue: string): string {
  return process.env[key] || defaultValue;
}

// Load configuration from environment variables
// Required variables will throw if not set
export const config: EnvironmentConfig = {
  api: {
    baseUrl: requireEnv('REACT_APP_API_BASE_URL'),
    telemetryUrl: requireEnv('REACT_APP_TELEMETRY_API_URL'),
    marketplaceUrl: requireEnv('REACT_APP_MARKETPLACE_API_URL'),
    timeout: parseInt(optionalEnv('REACT_APP_API_TIMEOUT', '30000')),
  },
  azure: {
    clientId: requireEnv('REACT_APP_AZURE_CLIENT_ID'),
    authority: optionalEnv('REACT_APP_AZURE_AUTHORITY', 'https://login.microsoftonline.com/common'),
    redirectUri: optionalEnv('REACT_APP_REDIRECT_URI', typeof window !== 'undefined' ? window.location.origin : ''),
    apiScope: requireEnv('REACT_APP_API_SCOPE'),
  },
  storage: {
    sharedAccount: requireEnv('REACT_APP_SHARED_STORAGE_ACCOUNT'),
  },
  features: {
    enableMsalLogging: process.env.REACT_APP_ENABLE_MSAL_LOGGING === 'true',
    enableApiLogging: process.env.REACT_APP_ENABLE_API_LOGGING === 'true',
    mockDataMode: process.env.REACT_APP_MOCK_DATA_MODE === 'true', // Only enable if explicitly set
    devMode: process.env.REACT_APP_DEV_MODE === 'true' && typeof window !== 'undefined' && window.location.hostname === 'localhost', // Only on localhost — never on deployed environments
  },
  environment: (process.env.REACT_APP_ENVIRONMENT as EnvironmentConfig['environment']) || 'development',
};

if (config.environment !== 'production') {
  const apiUrl = config.api.baseUrl || '';
  let detectedEnv = 'UNKNOWN';

  if (apiUrl.includes('aitryx-beta-core')) {
    detectedEnv = 'BETA';
  } else if (apiUrl.includes('aitryx-prod-core')) {
    detectedEnv = 'PRODUCTION';
  } else if (apiUrl.includes('aitryx-dev-core')) {
    detectedEnv = 'DEVELOPMENT';
  }

  // eslint-disable-next-line no-console
  console.log(`[Aitryx Partner] ${detectedEnv} — API: ${apiUrl}`);

  if (config.features.devMode) {
    // eslint-disable-next-line no-console
    console.warn('[Aitryx Partner] DEV MODE ENABLED — Authentication is bypassed. Local UI development only.');
  }
}

export default config;
